import React from "react";
import { Table } from "semantic-ui-react";

const hiddenKeys = ["id", "call_id", "user_id", "chat_id", "sms_id", "created_at", "updated_at"];

const parseJsonIfNeeded = (value) => {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (trimmed.startsWith("[") || trimmed.startsWith("{") || trimmed.includes('\\"')) {
    try {
      return JSON.parse(value);
    } catch (e) {
      try {
        const unescaped = value.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
        return JSON.parse(unescaped);
      } catch (e2) {
        return null;
      }
    }
  }

  return null;
};

const fieldLabelStyle = { fontSize: "11px", color: "#888", marginBottom: "4px", fontWeight: "500" };

const fieldValueStyle = {
  fontSize: "12px",
  color: "#333",
  wordBreak: "break-all",
  background: "#f8f9fa",
  padding: "8px 10px",
  borderRadius: "6px",
};

const codeBlockStyle = {
  ...fieldValueStyle,
  margin: 0,
  maxHeight: "240px",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "monospace",
};

// One labelled value on a delivery card.
const DetailField = ({ label, children }) => (
  <div>
    <div style={fieldLabelStyle}>{label}</div>
    {children}
  </div>
);

// "HTTP 500" (also when the transfer then failed), "No response" for a network error,
// or null for deliveries logged before codes were kept.
const httpOutcome = (item) => {
  if (item.http_code) return `HTTP ${item.http_code}`;
  if (item.error) return "No response";
  return null;
};

const formatDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

const prettyJson = (value) => (typeof value === "string" ? value : JSON.stringify(value, null, 2));

const renderWebhookItem = (item, index) => {
  const outcome = httpOutcome(item);
  const hasDuration = typeof item.duration_ms === "number";

  return (
    <Table.Row key={`webhook-item-${index}`}>
      <Table.Cell colSpan={2} style={{ padding: "16px", background: "#fafafa" }}>
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            border: "1px solid #e8e8e8",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "13px", fontWeight: "600", color: "#333" }}>
              Webhook #{index + 1}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <span
              style={{
                background: item.status === "success" ? "#d4edda" : "#f8d7da",
                color: item.status === "success" ? "#155724" : "#721c24",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "600",
                textTransform: "uppercase",
              }}
            >
              {item.status || "N/A"}
            </span>
            {outcome && <span style={{ fontSize: "12px", fontWeight: "600", color: "#333" }}>{outcome}</span>}
            {hasDuration && <span style={{ fontSize: "12px", color: "#888" }}>{formatDuration(item.duration_ms)}</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <DetailField label="WEBHOOK URL">
              <div style={{ ...fieldValueStyle, fontFamily: "monospace" }}>{item.webhook_url || "N/A"}</div>
            </DetailField>
            <DetailField label="CONTENT TYPE">
              <div style={fieldValueStyle}>{item.content_type || "N/A"}</div>
            </DetailField>
            {item.fallback == "yes" && (
              <DetailField label="FALLBACK">
                <div style={fieldValueStyle}>{item.fallback}</div>
              </DetailField>
            )}
            {item.error && (
              <DetailField label="ERROR">
                <div style={{ ...fieldValueStyle, color: "#721c24" }}>{item.error}</div>
              </DetailField>
            )}
            {item.response && (
              <DetailField label={item.response_truncated ? "RESPONSE (FIRST 2,000 BYTES)" : "RESPONSE"}>
                <pre style={codeBlockStyle}>{item.response}</pre>
              </DetailField>
            )}
            {item.payload && (
              <DetailField label={item.payload_truncated ? "PAYLOAD SENT (FIRST 8,000 BYTES)" : "PAYLOAD SENT"}>
                <pre style={codeBlockStyle}>{prettyJson(item.payload)}</pre>
              </DetailField>
            )}
            {item.payload_hidden && (
              <DetailField label="PAYLOAD SENT">
                <div style={fieldValueStyle}>Hidden because this account protects transcripts with a PIN.</div>
              </DetailField>
            )}
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

const renderValueRow = (rowKey, value) => {
  const parsedValue = parseJsonIfNeeded(value);

  if (parsedValue !== null && Array.isArray(parsedValue)) {
    return parsedValue.map((item, index) => renderWebhookItem(item, index));
  }

  return (
    <Table.Row key={rowKey}>
      <Table.Cell>
        <strong>{rowKey}</strong>
      </Table.Cell>
      <Table.Cell>
        {parsedValue !== null ? (
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(parsedValue, null, 2)}
          </pre>
        ) : (
          String(value)
        )}
      </Table.Cell>
    </Table.Row>
  );
};

// When a log row was written, as the server stored it. Deliveries in one row went out together.
const renderLoggedAt = (createdAt) => (
  <Table.Row>
    <Table.Cell colSpan={2} style={{ fontSize: "12px", color: "#555", fontWeight: "600" }}>
      Logged {createdAt}
    </Table.Cell>
  </Table.Row>
);

const EmptyState = () => (
  <div style={{ textAlign: "center" }}>
    <span style={{ color: "#666" }}>No data found.</span>
  </div>
);

const WebhookDataContent = ({ data }) => {
  if (data == null) {
    return <EmptyState />;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <EmptyState />;
    }

    return (
      <div className="row tracklogtable" style={{ maxHeight: "50vh", overflow: "auto", width: "100%" }}>
        <Table celled size="small">
          <Table.Body>
            {data.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {row.created_at && renderLoggedAt(row.created_at)}
                {Object.keys(row)
                  .filter((key) => !hiddenKeys.includes(key))
                  .map((key) => (
                    <React.Fragment key={`${rowIndex}-${key}`}>
                      {renderValueRow(key, row[key])}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);

    if (keys.length === 0) {
      return <EmptyState />;
    }

    return (
      <div className="row tracklogtable" style={{ maxHeight: "50vh", overflow: "auto", width: "100%" }}>
        <Table celled size="small">
          <Table.Body>
            {keys
              .filter((key) => !hiddenKeys.includes(key))
              .map((key) => (
                <React.Fragment key={key}>{renderValueRow(key, data[key])}</React.Fragment>
              ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  return <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(data)}</pre>;
};

export default WebhookDataContent;
